import React from 'react'

import {
  arrayOf,
  object,
  shape,
  string,
  bool,
} from 'prop-types'

import AiSparkleIcon from 'images/icons/svg/home/AiSparkleIcon'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import AiCard from './AiCard'
import CategoryColumn from './CategoryColumn'

// Maps a column to its grouped_live_search bucket: 3D Models reads `cg`, 3D Print Models `print_ready`.
const COLUMN_RESULT_KEYS = {
  cg: 'cg',
  print: 'print_ready',
}

// Turns [phrase, { total }] tuples into the { label, href, count } item shape CategoryColumn renders.
// href is a keyword search for the phrase; CategoryColumn localizes it. Falls back to the curated
// static items when the query yields nothing for this column.
function dynamicItems(entries, printReady) {
  return entries.map(([phrase, data]) => {
    const params = new URLSearchParams()
    params.set('keywords', phrase)
    if (printReady) {
      params.set('print_ready', '1')
    }

    return {
      label: phrase,
      href: `/search?${params.toString()}`,
      count: Number.isFinite(data?.total) ? data.total.toLocaleString() : undefined,
    }
  })
}

function HomeSearchDropdown({
  columns = EMPTY_ARRAY,
  aiCards = EMPTY_ARRAY,
  aiSectionTitle = '',
  ariaLabel = 'Search suggestions',
  groupedResults = null,
  isLoading = false,
  query = '',
}) {
  // Fall back to the curated static columns only when neither category has results (or no query yet).
  // If just one category is empty, that column shows an empty list rather than mixing in static data.
  const hasAnyResults = Object.values(COLUMN_RESULT_KEYS).some((key) => groupedResults?.[key]?.length)

  return (
    <div
      className="home-search-dropdown"
      id="home-search-dropdown"
      role="region"
      aria-label={ariaLabel}
      aria-busy={isLoading}
    >
      <div className="home-search-dropdown__columns">
        {columns.map((column) => {
          const entries = groupedResults?.[COLUMN_RESULT_KEYS[column.key]] || EMPTY_ARRAY
          const items = hasAnyResults ? dynamicItems(entries, column.printReady) : column.items

          return (
            <CategoryColumn
              key={column.key}
              columnKey={column.key}
              title={column.title}
              searchLabel={column.searchLabel}
              searchPath={column.searchPath}
              browsePath={column.browsePath}
              printReady={column.printReady}
              items={items}
              isLoading={isLoading}
              query={query}
            />
          )
        })}
      </div>

      {aiCards.length > 0 && (
        <div className="home-search-dropdown__ai">
          <div className="home-search-dropdown__ai-header">
            <AiSparkleIcon className="home-search-dropdown__ai-header-icon" aria-hidden="true" />
            <span className="home-search-dropdown__ai-header-title cgt-caption-semibold">{aiSectionTitle}</span>
          </div>
          <div className="home-search-dropdown__ai-cards">
            {aiCards.map((card) => (
              <AiCard
                key={card.mode}
                mode={card.mode}
                title={card.title}
                description={card.description}
                query={query}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

HomeSearchDropdown.propTypes = {
  columns: arrayOf(
    shape({
      key: string.isRequired,
      title: string.isRequired,
      searchLabel: string.isRequired,
      searchPath: string.isRequired,
      browsePath: string.isRequired,
      printReady: bool,
      items: arrayOf(shape({ label: string, href: string, count: string })),
    })
  ),
  aiCards: arrayOf(
    shape({
      mode: string.isRequired,
      title: string.isRequired,
      description: string.isRequired,
    })
  ),
  aiSectionTitle: string,
  ariaLabel: string,
  groupedResults: object,
  isLoading: bool,
  query: string,
}

export default HomeSearchDropdown
